/**
 * Runtime detection of which app surface (director vs. player) is being
 * served by the currently-loaded page.
 *
 * Driven by the resolved tenant's `surface` field (set when the host is
 * looked up in `lib/tenants.ts`). Multi-tenant aware — different tenants
 * can have different director / player hostnames, and the share-link
 * builder picks the player host that belongs to the same tenant.
 *
 * Phase 1 colocates both surfaces in a single React build; the helper
 * lets post-login, auth-callback, and already-signed-in redirects branch
 * by surface without separate bundles. When the apps are finally split
 * into separate builds, the helper can go away and each build can
 * hardcode its own surface.
 */

import {
  currentTenantOrNull,
  playerHostForCurrentTenant,
} from './tenants';

export function isPlayerApp(): boolean {
  return currentTenantOrNull()?.surface === 'player';
}

export function postLoginPath(): string {
  return isPlayerApp() ? '/player/dashboard' : '/dashboard';
}

export function profilePath(): string {
  return isPlayerApp() ? '/profile' : '/director/profile';
}

/**
 * Best-effort detection of the surface the *current page* belongs to.
 * Tenant config (`isPlayerApp`) takes precedence — that's the
 * authoritative signal once the host resolves to a tenant. The URL-path
 * fallback handles edge cases where the tenant might not be resolved
 * (shouldn't happen during normal flow but the helper is called from
 * surface-aware components that should still pick a sensible default).
 */
export function currentSurface(): 'player' | 'director' {
  if (isPlayerApp()) return 'player';
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/player/')
  ) {
    return 'player';
  }
  return 'director';
}

/**
 * URL for a tournament's public page, suitable for sharing on social media.
 * Always points at the player surface so the recipient lands on the app meant
 * for spectators and prospective entrants — never the director surface.
 *
 * - On the player app, or when the current tenant has no separate player
 *   host (single-host local dev), returns a relative path. Same-origin
 *   navigation, no protocol gymnastics needed.
 * - On the director app with a player host configured for the same
 *   tenant, returns an absolute URL that crosses to the player subdomain.
 *   Inherits the current page's protocol so the cookie/CORS posture
 *   matches.
 */
export function playerTournamentUrl(slug: string): string {
  const path = `/t/${slug}`;
  if (isPlayerApp() || typeof window === 'undefined') {
    return path;
  }
  const playerHost = playerHostForCurrentTenant();
  if (!playerHost) return path;
  return `${window.location.protocol}//${playerHost}${path}`;
}

/**
 * Always-absolute URL for a tournament's public report page, suitable for
 * encoding in a QR code shown on a TV/projector screen or printed in a PDF
 * — a relative path can't be scanned. `reportPath` is the public-page
 * suffix (e.g. `standings`, `pairings/3`, `starting-list`); omit it for
 * the tournament landing page.
 *
 * Resolves the player surface host the same way as `playerTournamentUrl`
 * (the spectator subdomain for white-label tenants), falling back to the
 * current host when there's no separate player host (single-host dev or
 * already on the player app).
 */
export function publicReportUrl(slug: string, reportPath = ''): string {
  const suffix = reportPath ? `/${reportPath}` : '';
  const path = `/t/${slug}${suffix}`;
  if (typeof window === 'undefined') return path;
  const host = isPlayerApp()
    ? window.location.host
    : (playerHostForCurrentTenant() ?? window.location.host);
  return `${window.location.protocol}//${host}${path}`;
}
